import * as React from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { SelectionAction } from "../../../ui/components/Table";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import * as smartLinkApi from "../../lib/api/smart-link";
import type { SmartLinkClaim } from "../../lib/api/smart-link";
import type { ListParams } from "../../../ui/api/listApi.types";
import { createSmartLinkClaimsTableColumns } from "./smartLinkClaimsTableColumns";
import { SmartLinkFullSyncModal } from "./SmartLinkFullSyncModal";
import { SmartLinkClaimCompareModal } from "./SmartLinkClaimCompareModal";
import { SmartLinkHealthSection } from "./SmartLinkHealthSection";
import * as pageStyles from "../users/UsersListPage.css";

export function SmartLinkUsersTab({ token }: { token: string }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [showFullSyncModal, setShowFullSyncModal] = React.useState(false);
  const [compareClaim, setCompareClaim] = React.useState<SmartLinkClaim | null>(null);
  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);

  const columns = React.useMemo(() => createSmartLinkClaimsTableColumns(t), [t]);
  const { confirm, ConfirmModal } = useConfirmModal();

  const selectionActions = React.useMemo<SelectionAction[]>(
    () => [
      {
        icon: <Trash2 size={14} />,
        label: t("pro.system.smartLink.delete.action"),
        danger: true,
        onClick: async (ids, names) => {
          const title =
            ids.length === 1 && names?.[0]
              ? t("pro.system.smartLink.delete.titleOne", { email: names[0] })
              : t("pro.system.smartLink.delete.titleMany", { count: ids.length });
          const ok = await confirm(
            title,
            t("pro.system.smartLink.delete.body"),
            t("pro.system.smartLink.delete.action"),
          );
          if (!ok) return;
          try {
            await smartLinkApi.deleteSmartLinkClaims(token, ids);
            refresh();
          } catch (err: any) {
            setLoadError(err?.message || t("pro.system.smartLink.delete.error"));
          }
        },
      },
    ],
    [t, confirm, token, refresh],
  );

  const fetchClaims = React.useCallback(
    async (params: ListParams) => {
      try {
        const result = await smartLinkApi.fetchSmartLinkClaims(token, params);
        setLoadError(null);
        return result;
      } catch (err: any) {
        setLoadError(err?.message || t("pro.system.smartLink.error.load"));
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, t],
  );

  return (
    <>
      <SmartLinkHealthSection entity="users" token={token} refreshKey={refreshKey} />

      {loadError && (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      )}

      <div className={pageStyles.tableHost}>
        <ListView<SmartLinkClaim>
          listId="smart-link-claims"
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={fetchClaims}
          contextKey={String(refreshKey)}
          fillContainer
          enabledViews={["table"]}
          searchPlaceholder={t("pro.system.smartLink.search")}
          emptyTitle={t("pro.system.smartLink.emptyTitle")}
          emptySubtitle={t("pro.system.smartLink.emptySubtitle")}
          defaultSort={{ sort_by: "lastSyncedAt", order: "desc" }}
          onRowClick={(row) => setCompareClaim(row)}
          getRowLabel={(row) => row.email}
          enableSelection
          selectionActions={selectionActions}
          headerActions={[
            {
              id: "full-sync",
              label: t("pro.system.smartLink.fullSync.button"),
              icon: <RefreshCw size={14} />,
              onClick: () => setShowFullSyncModal(true),
            },
          ]}
        />
      </div>

      {showFullSyncModal && (
        <SmartLinkFullSyncModal open={showFullSyncModal} setOpen={setShowFullSyncModal} onCommitted={refresh} />
      )}

      {compareClaim && (
        <SmartLinkClaimCompareModal
          claim={compareClaim}
          open={Boolean(compareClaim)}
          setOpen={(open) => {
            if (!open) setCompareClaim(null);
          }}
          onSynced={refresh}
        />
      )}

      <ConfirmModal />
    </>
  );
}
